import {
  AfterViewInit,
  Component,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {AG_GRID_LOCALE_EN, AG_GRID_LOCALE_IT, IoniconsOutline, WidgetValidator} from '@pt/pt-models';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ValidatorGenerator} from '../../utils';
import {ColDef, GridOptions} from "ag-grid-community";
import {ApiClientConfig} from "@pt/api-client";
import {CustomRendererComponent} from "../../../ag-grid";
import {ag_grid_frameworkComponents} from "../../../pt-shared.constant";
import {TranslateService} from "@ngx-translate/core";
import {MemberItem} from "it/primatoo/openapi/api/org/schemas/usergroup/MemberItem";
import {User as MenuUser} from "it/primatoo/openapi/api/org/schemas/user/listuserformenuresponse/User";

@Component({
  selector: 'pt-input-user-group-select',
  templateUrl: './input-user-group-select.component.html',
  styles: [`
    .ng-select.ng-select-multiple .ng-select-container .ng-value-container .ng-value .ng-value-label {
      padding: 4px 5px !important;
    }

    .user-group-select-container {
      max-width: 450px;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputUserGroupSelectComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputUserGroupSelectComponent),
      multi: true
    }
  ], encapsulation: ViewEncapsulation.None
})
export class InputUserGroupSelectComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit, ControlValueAccessor {
  @ViewChild('memberSelector', {static: true}) memberSelectorDialog: TemplateRef<unknown>;
  @ViewChild('actionCol', {static: true}) actionColTemplateRef: TemplateRef<unknown>;
  @ViewChild('nameCol', {static: true}) nameColTemplateRef: TemplateRef<unknown>;

  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label: string;
  @Input() placeholder: string;
  @Input() help: string;
  @Input() options: InputUserGroupSelectData<MenuUser>;
  @Input() disabled: boolean;

  public formGroup: FormGroup;
  public isRequired: boolean;
  public inputId: string;
  public dialogMembers: Array<MemberItem> = [];
  public dialogSelectedMembers: Array<MemberItem> = [];
  public selectedMembers: Array<MemberItem> = [];
  public gridOptions: GridOptions = null;
  public columnsDef: ColDef[];
  public IconRemove = IoniconsOutline.close;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;
  private readonly env;

  constructor(@Inject(ApiClientConfig) private config,
              private translate: TranslateService
  ) {
    this.env = this.config.env;
  }

  ngOnChanges(): void {
    this.alignSelect()
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.gridOptions = {
      ...this.env.defaultGridOptions,
      localeText: this.translate.currentLang == 'it' ? AG_GRID_LOCALE_IT : AG_GRID_LOCALE_EN,
      suppressPropertyNamesCheck: true,
      components: ag_grid_frameworkComponents,
    } as GridOptions;

    this.inputId = 'ptWUserGroupSelected_' + this.controlName;

    this.isRequired = ValidatorGenerator.validatorsContainsRequired(this.validators);

    this.setFormGroup();

    this.alignSelect();

    this.formGroup.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(t => {
      if (this.onChanged) {
        this.onChanged(t.value);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    });
  }

  writeValue(member: MemberItem[]): void {
    this.formGroup.controls.value.setValue(member, {emitEvent: false});
    this.selectedMembers = member;
    this.dialogMembers = this.toMemberItems(this.options ? this.options.items.filter((i: MenuUser) => !this.selectedMembers.find(m => m.userId === i.id)) : []);
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.controls.value.disable();
    } else {
      this.formGroup.controls.value.enable();
    }
  }

  validate() {
    return this.formGroup.invalid && {
      invalid: true
    };
  }

  removeMember(member: MemberItem) {
    const currentMember = this.selectedMembers.find(m => m.userId === member.userId);
    if (currentMember) {
      this.selectedMembers = this.selectedMembers.filter(m => m.userId != member.userId);
      this.formGroup.controls.value.setValue(this.selectedMembers);
    }
    this.alignSelect();
  }

  addMember() {
    if (this.dialogSelectedMembers.length > 0) {
      const actualSelectedMembers: MemberItem[] = this.formGroup.controls.value.value || [];
      actualSelectedMembers.push(...this.dialogSelectedMembers);
      this.formGroup.controls.value.setValue(actualSelectedMembers);
    }
    this.alignSelect();
  }

  customSearchFn(term: string, item: MemberItem) {
    term = term.toLowerCase();
    return item.fullName.indexOf(term) > -1 ||
      item.email.toLowerCase().indexOf(term) > -1;
  }

  ngAfterViewInit(): void {
    const colDef = [
      {
        field: "fullName",
        headerName: "Nome",
        hide: false,
        editable: false,
        suppressMenu: true,
        suppressNavigable: true,
        flex: 1,
        resizable: true,
        floatingFilter: false,
        sortable: true,
        colDrag: false,
        lockVisible: true,
        cellClass: 'no-border',
        cellRenderer: CustomRendererComponent,
        cellRendererParams: {
          ngTemplate: this.nameColTemplateRef
        }
      },
      {
        field: "email",
        headerName: "E-mail",
        hide: false,
        editable: false,
        flex: 1,
        suppressMenu: true,
        suppressNavigable: true,
        colDrag: false,
        resizable: true,
        floatingFilter: false,
        sortable: true,
        lockVisible: true,
      },
      {
        field: "associatedAt",
        headerName: "Data di Associazione",
        minWidth: 180,
        suppressMenu: true,
        suppressNavigable: true,
        colDrag: false,
        width: 180,
        cellRenderer: 'dateFormatterRenderer',
        resizable: true,
        sortable: true,
        hide: false,
        lockVisible: true,
      }, {
        headerName: '',
        field: 'actions',
        flex: 1,
        suppressMenu: true,
        width: 70,
        minWidth: 70,
        maxWidth: 70,
        suppressNavigable: true,
        colDrag: false,
        lockVisible: true,
        editable: false,
        cellClass: 'no-border',
        cellRenderer: CustomRendererComponent,
        cellRendererParams: {
          ngTemplate: this.actionColTemplateRef
        }
      }]
    this.columnsDef = colDef;
  }

  private setFormGroup() {
    const validator = ValidatorGenerator.generateValidator(this.validators);
    this.formGroup = new FormGroup({
      value: new FormControl(null, validator)
    });
  }

  private alignSelect() {
    if (this.formGroup) {
      this.dialogSelectedMembers = [];
      const alreadySelectedValue: MemberItem[] = this.formGroup.controls.value.value || [];
      this.dialogMembers = this.toMemberItems(this.options ? this.options.items.filter((i: MenuUser) => !alreadySelectedValue.find(u => u.userId === i.id)) : []);
      this.selectedMembers = JSON.parse(JSON.stringify(alreadySelectedValue)); // this.toMemberItems(this.options ? this.options.items.filter((i: UserItem) => alreadySelectedValue.includes(i.id)) : []);
    }

    if (this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.redrawRows();
    }
  }

  private toMemberItems(userItems: MenuUser[]): MemberItem[] {
    return userItems.map((u: MenuUser) => {
      return {
        userId: u.id,
        fullName: u.fullName,
        email: u.email,
        associatedAt: null
      };
    })
  }
}

export interface InputUserGroupSelectData<E> {
  bindLabel: string;
  bindValue: string;
  items: E[];
}
